import React from 'react'
import _cx from 'classnames/bind'
import { connect } from 'react-redux'
import configment from '@adc/configment'
import { Grid, Text, ChatLink, hooks } from '@adc/ui-adc'
import type { primitives } from '@adc/blueprint'
import { useListingContext } from '../../context/listing'
import SectionPanel from '../../components/sectionPanel'
// mobile web icons
import connectChat from '../../images/messages.png'
import connectEmail from '../../images/envelope.png'
import connectTalk from '../../images/user-headset.png'
import cls from './index.scss'

const cx = _cx.bind(cls)
const EMAIL_NEWLINE = '%0D%0A'
const elmId = 'help_center'
const phoneElmId = `${elmId}_email_phone`

interface PhoneNumberProps {
  isREO: boolean
}
const PhoneNumber = ({ isREO }: PhoneNumberProps) => {
  if (isREO) {
    return (
      <a href='tel:8007936107' data-elm-id={phoneElmId}>
        (800) 793-6107
      </a>
    )
  } else if (!isREO) {
    return (
      <a href='tel:8008519558' data-elm-id={phoneElmId}>
        (800) 851-9558
      </a>
    )
  } else {
    return (
      <a href='tel:8002802832' data-elm-id={phoneElmId}>
        (800) 280-2832
      </a>
    )
  }
}

interface HelpCenterProps {
  user: primitives.AuctionUser
}

const HelpCenter = ({ user }: HelpCenterProps) => {
  const { listing } = useListingContext()
  const isMobile = hooks.useIsMobileHook()
  const isREO = listing.auction?.is_online || false
  const emailSubject = `Question about ${String(listing.formatted_address)}`
  const emailBody = `I have a question about this property:${EMAIL_NEWLINE}${
    configment.get('AUCTIONCOM_HOST') + String(listing.listing_page_path)
  }${EMAIL_NEWLINE}`
  const desktopView = (
    <SectionPanel elmId='help_center'>
      <Grid>
        <Grid.Row>
          <Grid.Column computer={16} tablet={16}>
            <Text.Header
              className={cx('u-text-center')}
              // @ts-ignore fix types in DS, export enum if possible
              weight='normal'
              level={2}
            >
              Need Help?
            </Text.Header>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row centered>
          <Grid.Column computer={5} tablet={5}>
            <div className={cx('u-text-center', 'u-sm-mb-6')}>
              <div className='u-mb-2'>
                <img src={connectChat} alt='connect chat' />
              </div>
              <Text.Header level={3} className={cx('header')}>
                Chat with us
              </Text.Header>
              <Text>We're online</Text>
              <Text>Mon-Fri</Text>
              <Text>5am-5pm PT</Text>
              <Text>
                <a data-elm-id={`${elmId}_chat_now`}>
                  <ChatLink user={user} chatText='Chat Now' />
                </a>
              </Text>
            </div>
          </Grid.Column>
          <Grid.Column computer={5} tablet={5}>
            <div className={cx('u-text-center', 'u-sm-mb-6')}>
              <div className='u-mb-2'>
                <img src={connectEmail} alt='connect email' />
              </div>
              <Text.Header level={3} className={cx('header')}>
                Email us
              </Text.Header>
              <Text>For any questions,</Text>
              <Text>please send us a message</Text>
              <Text>
                <a
                  data-elm-id={`${elmId}_email`}
                  href={`mailto:customerservice@auction.com?subject=${emailSubject}&body=${emailBody}`}
                >
                  Get Started
                </a>
              </Text>
            </div>
          </Grid.Column>
          <Grid.Column computer={5} tablet={5}>
            <div className={cx('u-text-center', 'u-sm-mb-6')}>
              <div className='u-mb-2'>
                <img src={connectTalk} alt='connect talk' />
              </div>
              <Text.Header level={3} className={cx('header')}>
                Talk to us
              </Text.Header>
              <Text>
                <PhoneNumber isREO={isREO} />
              </Text>
              <Text>Mon-Fri</Text>
              <Text>5am-5pm PT</Text>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </SectionPanel>
  )
  const mobileView = (
    <SectionPanel elmId='help_center'>
      <Grid>
        <Grid.Row>
          <Grid.Column mobile={16}>
            <Text weight='normal' size='huge' className={cx('contact-title')}>
              Need Help?
            </Text>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column mobile={16}>
            <div className={cx('contact-row')}>
              <div className={cx('contact-image')}>
                <img src={connectChat} alt='connect chat' />
              </div>
              <div>
                <Text size='small' className={cx('mobile-header')}>
                  Chat with us.
                </Text>
                <Text size='tiny' color='gray'>
                  We're online Mon Fri 5am - 5pm PT
                </Text>
                <Text size='tiny'>
                  <a data-elm-id={`${elmId}_chat_now`}>
                    <ChatLink user={user} chatText='Chat Now' />
                  </a>
                </Text>
              </div>
            </div>
          </Grid.Column>
          <Grid.Column mobile={16}>
            <div className={cx('contact-row')}>
              <div className={cx('contact-image')}>
                <img src={connectEmail} alt='connect email' />
              </div>
              <div>
                <Text size='small' className={cx('mobile-header')}>
                  Email us.
                </Text>
                <Text size='tiny' color='gray'>
                  For any questions, please send us a message
                </Text>
                <Text size='tiny'>
                  <a
                    data-elm-id={`${elmId}_email`}
                    href={`mailto:customerservice@auction.com?subject=${emailSubject}&body=${emailBody}`}
                  >
                    Get Started
                  </a>
                </Text>
              </div>
            </div>
          </Grid.Column>
          <Grid.Column mobile={16}>
            <div className={cx('contact-row')}>
              <div className={cx('contact-image')}>
                <img src={connectTalk} alt='connect talk' />
              </div>
              <div>
                <Text size='small' className={cx('mobile-header')}>
                  Talk to us.
                </Text>
                <Text color='gray' size='tiny'>
                  Mon - Fri 5am - 5pm PT
                </Text>
                <Text size='tiny'>
                  <PhoneNumber isREO={isREO} />
                </Text>
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </SectionPanel>
  )
  return isMobile ? mobileView : desktopView
}

const mapStateToProps = (store: any) => ({
  user: store.user,
})

export default connect(mapStateToProps)(HelpCenter)
